import React, { useState, useEffect } from 'react'
import { useParams } from '@tanstack/react-router'
import DashboardLayout from '../../../components/DashboardLayout'
import { getOnboardingEmployeeById, getOnboardingSteps } from '@/lib/real-data-service'
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  FileText, 
  Video, 
  Users, 
  Monitor, 
  Calendar,
  Upload,
  Download,
  Mail,
  Phone,
  ArrowLeft,
  Edit,
  Play,
  User
} from 'lucide-react'

interface LocalOnboardingTask {
  id: string
  title: string
  description: string
  type: 'document' | 'training' | 'meeting' | 'system_access' | 'equipment' | 'video'
  status: 'pending' | 'in_progress' | 'completed' | 'overdue'
  dueDate: string
  completedDate?: string
  assignedTo: string
  documents?: Array<{
    name: string
    url: string
    required: boolean
    uploaded: boolean
  }>
  notes?: string
  dependencies?: string[]
}

interface LocalOnboardingEmployee {
  id: string
  name: string
  email: string
  phone: string
  position: string
  department: string
  manager: string
  contractType: 'CLT' | 'PJ'
  startDate: string
  status: 'not_started' | 'in_progress' | 'completed'
  progress: number
  avatar?: string
}

// Map real onboarding employee data
const mapToLocalEmployee = (employee: any): LocalOnboardingEmployee => {
  const progress = Math.round((employee.completedSteps / employee.totalSteps) * 100)
  
  return {
    id: employee.id,
    name: employee.name,
    email: employee.email,
    phone: '+55 11 99999-9999', // Would come from employee service in real implementation
    position: employee.role,
    department: employee.department,
    manager: employee.manager || 'TBD',
    contractType: 'PJ', // Would be determined from real employee data
    startDate: employee.startDate,
    status: employee.onboardingStatus,
    progress,
    avatar: employee.name.split(' ').map((n: string) => n[0]).join('')
  }
}

// Map real onboarding step to local task
const mapToLocalTask = (step: any, index: number): LocalOnboardingTask => {
  const baseDate = new Date()
  baseDate.setDate(baseDate.getDate() + index)
  
  const getTaskType = (category: string) => {
    switch (category) {
      case 'documentation': return 'document'
      case 'training': return 'training'
      case 'meeting': return 'meeting'
      case 'system': return 'system_access'
      default: return 'video'
    }
  }
  
  const getAssignedTo = (category: string) => {
    switch (category) {
      case 'documentation': return 'RH'
      case 'training': return 'RH'
      case 'meeting': return 'Manager'
      case 'system': return 'TI'
      default: return 'RH'
    }
  }
  
  return {
    id: step.id,
    title: step.title,
    description: step.description,
    type: getTaskType(step.category),
    status: step.status,
    dueDate: baseDate.toISOString().split('T')[0],
    completedDate: step.status === 'completed' ? baseDate.toISOString().split('T')[0] : undefined,
    assignedTo: getAssignedTo(step.category),
    notes: step.resources ? `Recursos disponíveis: ${step.resources.join(', ')}` : undefined,
    dependencies: step.dependencies
  }
}

// This will be populated with real data below in the component

export default function OnboardingDetailPage() {
  const { id } = useParams({ from: '/_auth/onboarding/$id' })
  const [selectedTask, setSelectedTask] = useState<LocalOnboardingTask | null>(null)
  const [showNotes, setShowNotes] = useState<string | null>(null)

  // Loading states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data states
  const [employee, setEmployee] = useState<LocalOnboardingEmployee | null>(null)
  const [tasks, setTasks] = useState<LocalOnboardingTask[]>([])

  // Load employee and onboarding data asynchronously
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('No employee ID provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const realEmployee = await getOnboardingEmployeeById(id)
        if (!realEmployee) {
          setError('Employee not found')
          setLoading(false)
          return
        }

        const mappedEmployee = mapToLocalEmployee(realEmployee)
        const realSteps = await getOnboardingSteps(realEmployee.role)
        const mappedTasks = realSteps.map(mapToLocalTask)

        setEmployee(mappedEmployee)
        setTasks(mappedTasks)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load onboarding data')
        console.error('Error loading onboarding detail data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [id])

  const getTaskIcon = (type: string) => {
    const icons = {
      document: FileText,
      training: Monitor,
      meeting: Users,
      system_access: User,
      equipment: Monitor,
      video: Video
    }
    const Icon = icons[type as keyof typeof icons] || FileText
    return <Icon size={16} />
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-800 border-gray-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      overdue: 'bg-red-100 text-red-800 border-red-300'
    }
    return styles[status as keyof typeof styles] || styles.pending
  }

  const getStatusText = (status: string) => {
    const texts = {
      pending: 'Pendente',
      in_progress: 'Em Progresso',
      completed: 'Concluído',
      overdue: 'Atrasado'
    }
    return texts[status as keyof typeof texts] || 'Pendente'
  }

  const getStatusIcon = (status: string) => {
    const icons = {
      pending: <Clock className="w-4 h-4 text-gray-500" />,
      in_progress: <Clock className="w-4 h-4 text-blue-500" />,
      completed: <CheckCircle className="w-4 h-4 text-green-500" />,
      overdue: <AlertCircle className="w-4 h-4 text-red-500" />
    }
    return icons[status as keyof typeof icons] || icons.pending
  }

  const completedTasks = tasks.filter(task => task.status === 'completed').length
  const totalTasks = tasks.length
  const progressPercentage = Math.round((completedTasks / totalTasks) * 100)

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mb-4"></div>
            <p className="text-gray-900 text-lg">Carregando dados do onboarding...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="text-center py-12">
            <div className="text-red-600 text-lg mb-2">Erro ao carregar dados</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // Data not loaded
  if (!employee) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="text-center py-12">
            <p className="text-gray-600">Funcionário não encontrado</p>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors mt-4"
            >
              Voltar
            </button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Onboarding - {employee?.name || 'Colaborador'}</h1>
              <p className="text-gray-600 mt-1">{employee?.position} • {employee?.department}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2">
              <Edit size={16} />
              Editar
            </button>
          </div>
        </div>

        {/* Employee Info Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <p className="text-sm text-gray-600">Email:</p>
                  <p className="font-medium">{employee?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Telefone:</p>
                  <p className="font-medium">{employee?.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gestor:</p>
                  <p className="font-medium">{employee?.manager}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Data de Início:</p>
                  <p className="font-medium">{employee?.startDate ? new Date(employee.startDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="mb-2">
                <span className="text-2xl font-bold text-emerald-600">{progressPercentage}%</span>
                <p className="text-sm text-gray-600">Concluído</p>
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-3">
                <div
                  className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {completedTasks} de {totalTasks} tarefas
              </p>
            </div>
          </div>
        </div>

        {/* Tasks Checklist */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Checklist de Integração</h2>
            <p className="text-gray-600 mt-1">Acompanhe o progresso de cada etapa do onboarding</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {tasks.map((task, index) => (
              <div key={task.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start space-x-4">
                  {/* Step Number */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    task.status === 'completed' 
                      ? 'bg-green-100 text-green-800' 
                      : task.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {task.status === 'completed' ? <CheckCircle size={16} /> : index + 1}
                  </div>

                  {/* Task Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <div className="flex items-center space-x-1 text-gray-500">
                          {getTaskIcon(task.type)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(task.status)}`}>
                          {getStatusIcon(task.status)}
                          <span className="ml-1">{getStatusText(task.status)}</span>
                        </span>
                        <span className="text-sm text-gray-500">
                          Prazo: {new Date(task.dueDate).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mt-1">{task.description}</p>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Responsável: {task.assignedTo}</span>
                        {task.completedDate && (
                          <span>Concluído: {new Date(task.completedDate).toLocaleDateString('pt-BR')}</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {task.documents && task.documents.length > 0 && (
                          <button 
                            onClick={() => setSelectedTask(task)}
                            className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1"
                          >
                            <FileText size={14} />
                            {task.documents.length} documento(s)
                          </button>
                        )}
                        {task.notes && (
                          <button 
                            onClick={() => setShowNotes(showNotes === task.id ? null : task.id)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Ver notas
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {showNotes === task.id && task.notes && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">{task.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Documents Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Documentos - {selectedTask.title}</h2>
                <button
                  onClick={() => setSelectedTask(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3">
                {selectedTask.documents?.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.required ? 'Obrigatório' : 'Opcional'} • 
                          {doc.uploaded ? ' Enviado' : ' Pendente'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {doc.uploaded ? (
                        <button className="text-emerald-600 hover:text-emerald-700 p-2">
                          <Download size={16} />
                        </button>
                      ) : (
                        <button className="text-gray-400 hover:text-gray-600 p-2">
                          <Upload size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
} 